import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { ToggleGroup, ToggleItem } from '../ui/toggle';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem } from '../ui/dropdown-menu';
import { ChevronDown, Flag, HelpCircle, Search, X } from 'lucide-react';
import { flaggedChatsAPI } from '../../services/api';
import { flagTypeHex } from './flagTypeColors';
import { agentLabel, BOT_STATUS_UNKNOWN_NOTICE, THREAD_METRICS_FEATURE } from './agentLabels';
import { useBrand } from '../../context/BrandContext';
import { downloadFlaggedThreadsCsv, downloadFlaggedThreadsJson } from './flaggedChatsExport';
import AgentAnalysisTab from './AgentAnalysisTab';
import DateRangeFilter from './DateRangeFilter';
import FlagTypeFilter from './FlagTypeFilter';
import FlaggedThreadRow from './ThreadRow';

const TABS = [
  { value: 'threads', label: 'Flagged Conversations' },
  { value: 'agent-analysis', label: 'Agent Analysis' },
];

// The column to sort by — direction is a separate control (sortDir), so every column here
// supports both ascending and descending. The `metric` ones are only offered to brands with
// conversation-length metrics (see THREAD_METRICS_FEATURE).
const SORT_BY_OPTIONS = [
  { value: 'start_at', label: 'Date' },
  { value: 'flags', label: '# Flags' },
  { value: 'messages', label: '# Messages', metric: true },
  { value: 'words', label: '# Words', metric: true },
  { value: 'duration', label: 'Duration', metric: true },
];

const THREADS_PAGE_SIZE = 25;

// -- Filter chrome ------------------------------------------------------------

const FilterSelect = ({ label, value, onChange, options, getOptionLabel, labelClassName = '', hint }) => (
  <div className="flex items-center gap-1.5">
    <span className={`font-mono text-[10px] uppercase tracking-wider text-ink-mute ${labelClassName}`}>{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-48 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{getOptionLabel ? getOptionLabel(o) : o.value}</SelectItem>)}
      </SelectContent>
    </Select>
    {hint && (
      <span className="shrink-0 text-ink-mute" title={hint} aria-label={hint}>
        <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    )}
  </div>
);

// Same look as FilterSelect, but for OR filters where more than one value can be picked at
// once (e.g. Country — a thread only has one, but a reviewer often wants "any of these few").
// The trigger summarizes the selection instead of showing a single value, and the dropdown
// stays open across clicks (onSelect prevents Radix's default close-on-select) so multiple
// options can be toggled in one pass.
const MultiFilterSelect = ({ label, values, onToggle, onClear, options, getOptionLabel, labelClassName = '' }) => {
  const summary = values.length === 0
    ? 'All'
    : values.length === 1
      ? (getOptionLabel ? getOptionLabel(options.find((o) => o.value === values[0]) || { value: values[0] }) : values[0])
      : `${values.length} selected`;
  return (
    <div className="flex items-center gap-1.5">
      <span className={`font-mono text-[10px] uppercase tracking-wider text-ink-mute ${labelClassName}`}>{label}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-8 w-48 items-center justify-between rounded-lg border border-white/10 bg-surface-2 px-3 text-xs font-medium text-ink focus:outline-none focus:border-cta focus:ring-1 focus:ring-cta"
          >
            <span className="truncate">{summary}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ink-dim" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {values.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="w-full px-2 py-1 text-left text-[10px] text-ink-mute hover:text-ink underline"
            >
              Clear
            </button>
          )}
          {options.map((o) => (
            <DropdownMenuCheckboxItem
              key={o.value}
              checked={values.includes(o.value)}
              onSelect={(e) => { e.preventDefault(); onToggle(o.value); }}
            >
              {getOptionLabel ? getOptionLabel(o) : o.value}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Exact-match lookup, not a live/fuzzy content search (unlike CRM Customer Intelligence's
// debounced search box) — submits on Enter rather than as-you-type, since a partial thread ID
// isn't a meaningful query. Submitting clears every other filter first (the caller's
// onSearch), so the result is either "this one thread" or "no flags for this thread ID".
const ThreadIDSearch = ({ value, onChange, onSearch, onClear }) => (
  <div className="relative w-64">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-mute" />
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') onSearch(); }}
      placeholder="Search by conversation ID…"
      className="pl-9 pr-9 h-8 text-xs"
    />
    {value && (
      <button
        type="button"
        onClick={onClear}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-mute hover:text-ink"
      >
        <X className="h-4 w-4" />
      </button>
    )}
  </div>
);

// Bulk download of every thread matching the report's current filters (not just the current
// page) — mirrors livechat_mining/PatternExportButtons: CSV is metadata-only (one row per
// flag, same layout as the single-thread export), JSON carries every matching thread's full
// conversation. Both hit the same uncapped-but-truncatable export endpoint.
const BulkExportButtons = ({
  from, to, flagTypes, agent, sortBy, sortDir, checkedFilter, ratingFilter, hasCommentFilter, countryFilters,
  checkedByFilter, threadIDFilter,
}) => {
  const [loadingKind, setLoadingKind] = useState(null);
  const [error, setError] = useState('');
  const [truncated, setTruncated] = useState(false);

  const runExport = async (kind) => {
    try {
      setLoadingKind(kind);
      setError('');
      setTruncated(false);
      const resp = await flaggedChatsAPI.exportThreads({
        from, to, flagTypes, agent, sortBy, sortDir, checked: checkedFilter, rating: ratingFilter,
        hasComment: hasCommentFilter, countries: countryFilters, checkedBy: checkedByFilter, threadID: threadIDFilter,
      });
      const threads = resp.data.threads || [];
      if (kind === 'csv') downloadFlaggedThreadsCsv(threads);
      else downloadFlaggedThreadsJson(threads);
      if (resp.data.truncated) setTruncated(true);
    } catch {
      setError('Export failed.');
    } finally {
      setLoadingKind(null);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1 mb-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" disabled={loadingKind != null} onClick={() => runExport('csv')}>
          {loadingKind === 'csv' ? 'Exporting…' : 'Export metadata (CSV)'}
        </Button>
        <Button variant="outline" size="sm" disabled={loadingKind != null} onClick={() => runExport('json')}>
          {loadingKind === 'json' ? 'Exporting…' : 'Export conversations (JSON)'}
        </Button>
        {error && <span className="text-[11px] text-negative">{error}</span>}
      </div>
      {truncated && (
        <span className="text-[11px] text-warning">
          More conversations match these filters than fit in one export — narrow your filters to get the rest.
        </span>
      )}
    </div>
  );
};

const PaginationFooter = ({ total, page, totalPages, onPageChange }) => {
  if (total === 0) return null;
  return (
    <div className="flex items-center justify-between mt-3">
      <span className="text-[11px] text-ink-dim">{total} total</span>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>← Prev</Button>
        <span className="text-[11px] text-ink-dim">{page} / {totalPages}</span>
        <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>Next →</Button>
      </div>
    </div>
  );
};

// -- Page -----------------------------------------------------------------------

function FlaggedChatsPage() {
  const { activeBrandInfo } = useBrand();
  const hasThreadMetrics = !(activeBrandInfo?.unsupported || []).includes(THREAD_METRICS_FEATURE);
  const sortByOptions = SORT_BY_OPTIONS.filter((o) => hasThreadMetrics || !o.metric);
  const [activeTab, setActiveTab] = useState('threads');
  const [dateBounds, setDateBounds] = useState(null);
  const [range, setRange] = useState(null);
  const [flagTypes, setFlagTypes] = useState([]);
  const [agents, setAgents] = useState([]);
  // Defaults to true so the caveat only ever appears once the API has actually reported that
  // bot classification is missing — not during the initial load, when nothing is known yet.
  const [botStatusKnown, setBotStatusKnown] = useState(true);
  const [flagTypeFilters, setFlagTypeFilters] = useState([]);
  const [agentFilter, setAgentFilter] = useState('all');
  const [checkedFilter, setCheckedFilter] = useState('');
  const [checkedByOptions, setCheckedByOptions] = useState([]);
  const [checkedByFilter, setCheckedByFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [hasCommentFilter, setHasCommentFilter] = useState('');
  const [countries, setCountries] = useState([]);
  const [countryFilters, setCountryFilters] = useState([]);
  const [threadIDSearch, setThreadIDSearch] = useState('');
  const [threadIDFilter, setThreadIDFilter] = useState('');
  const [sortBy, setSortBy] = useState('start_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [flagTypeCounts, setFlagTypeCounts] = useState([]);
  const [expandedThreadId, setExpandedThreadId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter option lists + the default "all time" range only need loading once.
  useEffect(() => {
    (async () => {
      try {
        const [boundsResp, flagTypesResp, agentsResp, checkedByResp, countriesResp] = await Promise.all([
          flaggedChatsAPI.getDateBounds(),
          flaggedChatsAPI.getFlagTypes(),
          flaggedChatsAPI.getAgents(),
          flaggedChatsAPI.getCheckedByOptions(),
          flaggedChatsAPI.getCountries(),
        ]);
        const bounds = boundsResp.data;
        setDateBounds(bounds);
        if (bounds.min_start_at && bounds.max_start_at) {
          setRange({ from: bounds.min_start_at, to: bounds.max_start_at });
        } else {
          setRange({ from: null, to: null });
        }
        setFlagTypes(flagTypesResp.data.flag_types || []);
        setAgents(agentsResp.data.agents || []);
        setBotStatusKnown(agentsResp.data.bot_status_known !== false);
        setCheckedByOptions(checkedByResp.data.checked_by || []);
        setCountries(countriesResp.data.countries || []);
      } catch {
        setError('Failed to load filters.');
        setRange({ from: null, to: null });
      }
    })();
  }, []);

  useEffect(() => {
    if (!range) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const agent = agentFilter === 'all' ? '' : agentFilter;
        const resp = await flaggedChatsAPI.getThreads({
          from: range.from, to: range.to, flagTypes: flagTypeFilters, agent, sortBy, sortDir,
          checked: checkedFilter, rating: ratingFilter, hasComment: hasCommentFilter, countries: countryFilters,
          checkedBy: checkedByFilter, threadID: threadIDFilter, page, pageSize: THREADS_PAGE_SIZE,
        });
        if (cancelled) return;
        setThreads(resp.data.threads || []);
        setTotal(resp.data.total || 0);
        setFlagTypeCounts(resp.data.flag_type_counts || []);
      } catch {
        if (!cancelled) setError('Failed to load flagged chats.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [
    range, flagTypeFilters, agentFilter, checkedFilter, checkedByFilter, ratingFilter, hasCommentFilter,
    countryFilters, threadIDFilter, sortBy, sortDir, page,
  ]);

  const toggleThread = useCallback((threadId) => {
    setExpandedThreadId((prev) => (prev === threadId ? null : threadId));
  }, []);

  const handleRangeChange = (next) => {
    setRange(next);
    setPage(1);
  };

  const handleFlagTypeToggle = (flagType) => {
    setFlagTypeFilters((prev) => (
      prev.includes(flagType) ? prev.filter((ft) => ft !== flagType) : [...prev, flagType]
    ));
    setPage(1);
  };

  const handleFlagTypeClear = () => {
    setFlagTypeFilters([]);
    setPage(1);
  };

  const handleAgentChange = (value) => {
    setAgentFilter(value);
    setPage(1);
  };

  const handleCheckedFilterChange = (value) => {
    if (value === undefined) return;
    setCheckedFilter(value);
    // "Checked by" only applies (and is only shown) alongside the "checked" status — reset it
    // so a stale reviewer filter doesn't silently carry over next time Checked is reselected.
    if (value !== 'checked') setCheckedByFilter('');
    setPage(1);
  };

  const handleCheckedByFilterChange = (value) => {
    setCheckedByFilter(value === 'all' ? '' : value);
    setPage(1);
  };

  const handleRatingFilterChange = (value) => {
    if (value === undefined) return;
    setRatingFilter(value);
    setPage(1);
  };

  const handleHasCommentFilterChange = (value) => {
    if (value === undefined) return;
    setHasCommentFilter(value);
    setPage(1);
  };

  const handleCountryFilterToggle = (country) => {
    setCountryFilters((prev) => (
      prev.includes(country) ? prev.filter((c) => c !== country) : [...prev, country]
    ));
    setPage(1);
  };

  const handleCountryFilterClear = () => {
    setCountryFilters([]);
    setPage(1);
  };

  // Searching by thread ID is a different kind of question ("did this one thread have any
  // flags?") than the filtered list above it — every other filter is cleared and the date
  // range widened to all time so the search always finds the thread if it exists, rather
  // than silently returning nothing because of a leftover filter the user forgot about.
  const handleThreadIDSearchSubmit = () => {
    const id = threadIDSearch.trim();
    setFlagTypeFilters([]);
    setAgentFilter('all');
    setCheckedFilter('');
    setCheckedByFilter('');
    setRatingFilter('');
    setHasCommentFilter('');
    setCountryFilters([]);
    setSortBy('start_at');
    setSortDir('desc');
    if (dateBounds?.min_start_at && dateBounds?.max_start_at) {
      setRange({ from: dateBounds.min_start_at, to: dateBounds.max_start_at });
    }
    setThreadIDFilter(id);
    setPage(1);
  };

  const handleThreadIDSearchClear = () => {
    setThreadIDSearch('');
    setThreadIDFilter('');
    setPage(1);
  };

  const handleSortByChange = (value) => {
    if (!value) return;
    setSortBy(value);
    setPage(1);
  };

  const handleSortDirChange = (value) => {
    if (!value) return;
    setSortDir(value);
    setPage(1);
  };

  // Shared across every reviewer (not per-user), so a successful toggle here is final — no
  // "your view" vs "everyone's view" to reconcile. Updates local state directly instead of
  // refetching: if the new checked value no longer matches the active checkedFilter, the
  // thread drops out of the list immediately, same as a fresh fetch would show.
  const handleToggleChecked = async (threadId, checked, startAt) => {
    let checkedBy;
    try {
      const resp = await flaggedChatsAPI.setThreadChecked(threadId, checked, startAt);
      checkedBy = resp.data.checked_by;
    } catch {
      setError('Failed to update checked status.');
      return;
    }
    const stillMatches = checkedFilter === '' || (checkedFilter === 'checked') === checked;
    if (stillMatches) {
      setThreads((prev) => prev.map((t) => (
        t.thread_id === threadId ? { ...t, checked, checked_by: checked ? checkedBy : null } : t
      )));
    } else {
      setThreads((prev) => prev.filter((t) => t.thread_id !== threadId));
      setTotal((prev) => Math.max(0, prev - 1));
    }
  };

  const agentOptions = useMemo(
    () => agents.map((a) => ({ value: a.agent_id, is_bot: a.is_bot })),
    [agents],
  );

  const totalPages = Math.max(1, Math.ceil(total / THREADS_PAGE_SIZE));

  return (
    <div className="max-w-6xl mx-auto py-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ink">Flagged Chats</h2>
        <p className="text-sm text-ink-dim mt-1">
          Chats flagged by customer support, for senior review and analysis.
        </p>
      </div>

      <div className="mb-6">
        <ToggleGroup value={activeTab} onChange={setActiveTab}>
          {TABS.map((t) => <ToggleItem key={t.value} value={t.value}>{t.label}</ToggleItem>)}
        </ToggleGroup>
      </div>

      {activeTab === 'agent-analysis' ? (
        <AgentAnalysisTab />
      ) : (
        <>
          {/* Not sticky — the headline count and per-flag breakdown are a snapshot of the
              current filters, not something the user needs pinned while scrolling through
              threads, unlike the filter/export chrome below. */}
          <div className="mb-4 flex flex-col sm:flex-row items-stretch gap-3">
            {/* Kept as its own block, separate from the per-flag breakdown — it's the
                headline number for the whole filtered set, not one more entry among them. */}
            <div className="shrink-0 rounded-lg border border-white/10 bg-surface-1 px-4 py-2 flex flex-col justify-center">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">Flagged conversations</div>
              <div className="text-2xl font-bold text-ink mt-0.5">{total}</div>
            </div>

            {/* Per-flag breakdown across the current filters (not scoped by checked
                status), most-common first — same ranking "Most flagged" sorts by. A real
                CSS grid (not flex-wrap) so the divider lines between cells stay aligned
                across every row, however many flag types wrap onto a second/third line —
                flex-wrap can't guarantee that, since row heights/widths aren't shared
                columns. gap-px + the container's own bg shows through as the grid lines. */}
            {flagTypeCounts.length > 0 && (
              <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-white/10 rounded-lg border border-white/10 overflow-hidden">
                {flagTypeCounts.map((c) => (
                  <div key={c.flag_type} className="bg-surface-1 px-3 py-1.5 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span
                        className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: flagTypeHex(c.flag_type) }}
                      />
                      <span className="font-mono text-[9px] uppercase tracking-wider text-ink-mute truncate">{c.flag_type}</span>
                    </div>
                    <div className="text-sm font-bold text-ink mt-0.5">{c.thread_count}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sticky — just the date range/search and flag type filter. The agent/sort/status/
              rating/comment filters and export buttons below are not pinned. py-4 (not just
              pb-4) so there's breathing room above the date range once this is actually stuck
              to the top, matching the gap the flag types already get below them. */}
          <div className="sticky top-14 z-20 bg-surface py-4 mb-6 border-b border-white/5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <DateRangeFilter range={range} bounds={dateBounds} onChange={handleRangeChange} />
              <ThreadIDSearch
                value={threadIDSearch} onChange={setThreadIDSearch}
                onSearch={handleThreadIDSearchSubmit} onClear={handleThreadIDSearchClear}
              />
            </div>

            <div className="mt-4">
              <FlagTypeFilter
                flagTypes={flagTypes} selected={flagTypeFilters} label="Flag types (must have all selected)"
                onToggle={handleFlagTypeToggle} onClear={handleFlagTypeClear}
              />
            </div>
          </div>

          {error && <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

          <div className="mb-3 flex items-center gap-3 flex-wrap">
            <div className="w-72 shrink-0">
              <FilterSelect
                label="Agent" value={agentFilter} onChange={handleAgentChange} options={agentOptions}
                getOptionLabel={(o) => agentLabel(o.value)}
                labelClassName="inline-block w-11"
                hint={botStatusKnown ? undefined : BOT_STATUS_UNKNOWN_NOTICE}
              />
            </div>
            {/* w-96 shrink-0 (not just content-sized) so this block's width matches Rating's
                below it exactly — otherwise the two would naturally size to their own content
                (Sort by's Select+ToggleGroup vs. Rating's wider 4-item ToggleGroup) and the
                Country/Comment divider after them would land at two different x positions. */}
            <div className="w-96 shrink-0 flex items-center gap-1.5 -ml-[19px] pl-[31px] border-l border-white/10">
              <span className="inline-block w-12 font-mono text-[10px] uppercase tracking-wider text-ink-mute">Sort by</span>
              <Select value={sortBy} onValueChange={handleSortByChange}>
                <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sortByOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <ToggleGroup value={sortDir} onChange={handleSortDirChange}>
                <ToggleItem value="desc">Desc</ToggleItem>
                <ToggleItem value="asc">Asc</ToggleItem>
              </ToggleGroup>
            </div>
            <div className="ml-4 pl-7 border-l border-white/10">
              <MultiFilterSelect
                label="Country" values={countryFilters} onToggle={handleCountryFilterToggle}
                onClear={handleCountryFilterClear}
                options={countries.map((c) => ({ value: c }))}
                labelClassName="inline-block w-11"
              />
            </div>
          </div>

          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <div className="w-72 shrink-0 flex items-center gap-1.5">
              <span className="inline-block w-11 font-mono text-[10px] uppercase tracking-wider text-ink-mute">Status</span>
              <ToggleGroup value={checkedFilter} onChange={handleCheckedFilterChange}>
                <ToggleItem value="">All</ToggleItem>
                <ToggleItem value="checked">Checked</ToggleItem>
                <ToggleItem value="unchecked">Unchecked</ToggleItem>
              </ToggleGroup>
            </div>
            <div className="w-96 shrink-0 flex items-center gap-1.5 -ml-[19px] pl-[31px] border-l border-white/10">
              <span className="inline-block w-12 font-mono text-[10px] uppercase tracking-wider text-ink-mute">Rating</span>
              <ToggleGroup value={ratingFilter} onChange={handleRatingFilterChange}>
                <ToggleItem value="">Any</ToggleItem>
                <ToggleItem value="positive">Positive</ToggleItem>
                <ToggleItem value="negative">Negative</ToggleItem>
                <ToggleItem value="not_rated">Not rated</ToggleItem>
              </ToggleGroup>
            </div>
            <div className="flex items-center gap-2 ml-4 pl-7 border-l border-white/10">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">Comment</span>
              <ToggleGroup value={hasCommentFilter} onChange={handleHasCommentFilterChange}>
                <ToggleItem value="">Any</ToggleItem>
                <ToggleItem value="yes">Yes</ToggleItem>
                <ToggleItem value="no">No</ToggleItem>
              </ToggleGroup>
            </div>
          </div>

          {checkedFilter === 'checked' && (
            <div className="mb-4 w-72">
              <FilterSelect
                label="Checked by" value={checkedByFilter || 'all'} onChange={handleCheckedByFilterChange}
                options={checkedByOptions.map((email) => ({ value: email }))}
              />
            </div>
          )}

          {range && (
            <BulkExportButtons
              from={range.from} to={range.to} flagTypes={flagTypeFilters}
              agent={agentFilter === 'all' ? '' : agentFilter}
              sortBy={sortBy} sortDir={sortDir} checkedFilter={checkedFilter}
              ratingFilter={ratingFilter} hasCommentFilter={hasCommentFilter} countryFilters={countryFilters}
              checkedByFilter={checkedByFilter}
              threadIDFilter={threadIDFilter}
            />
          )}

          <Card>
            <CardContent className="p-4">
              {loading ? (
                <div className="flex justify-center py-8"><Spinner /></div>
              ) : (
                <>
                  <div className="divide-y divide-white/5">
                    {threads.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 gap-2">
                        <Flag className="h-6 w-6 text-ink-dim mb-1" />
                        <p className="text-sm font-bold text-ink">No flagged chats</p>
                        <p className="text-xs text-ink-mute">Nothing matches the current filters.</p>
                      </div>
                    ) : threads.map((t) => (
                      <FlaggedThreadRow
                        key={t.thread_id}
                        thread={t}
                        isExpanded={expandedThreadId === t.thread_id}
                        onToggle={toggleThread}
                        onToggleChecked={handleToggleChecked}
                      />
                    ))}
                  </div>
                  <PaginationFooter total={total} page={page} totalPages={totalPages} onPageChange={setPage} />
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// Remounts the page whenever the brand switches. Everything on it belongs to one brand — the
// date bounds, the option lists, every selected filter — so just refetching would keep the
// previous brand's selections (an agent or a pattern that doesn't exist there). The X-Brand
// header is already switched by the time the new page fetches: App's BrandSync renders ahead of
// the routes, so its effect runs first.
function FlaggedChatsPageForBrand() {
  const { activeBrand } = useBrand();
  return <FlaggedChatsPage key={activeBrand} />;
}

export default FlaggedChatsPageForBrand;
